import { useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/use-auth";
import { ChevronRight, ChevronLeft, CalendarDays, Loader2 } from "lucide-react";

type CalendarView = "day" | "week" | "month";

interface BookingEntry {
  readonly id: string;
  readonly serviceId: string;
  readonly serviceName: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly bookingType: string;
  readonly status: string;
  readonly customerName: string;
  readonly customerPhone: string | null;
  readonly notes: string | null;
  readonly source: string;
}

interface CalendarResponse {
  readonly success: boolean;
  readonly data: {
    readonly bookings: Record<string, readonly BookingEntry[]>;
    readonly totalCount: number;
  };
}

const HEBREW_DAYS = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"] as const;
const HEBREW_DAYS_FULL = ["יום ראשון", "יום שני", "יום שלישי", "יום רביעי", "יום חמישי", "יום שישי", "שבת"] as const;
const HEBREW_MONTHS = ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"] as const;

const TIME_SLOTS = Array.from({ length: 25 }, (_, i) => {
  const hour = 8 + Math.floor(i / 2);
  const min = i % 2 === 0 ? "00" : "30";
  return `${String(hour).padStart(2, "0")}:${min}`;
}).filter((t) => t <= "20:00");

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + n);
  return result;
}

function getWeekStart(d: Date): Date {
  const result = new Date(d);
  const day = result.getDay();
  result.setDate(result.getDate() - day); // Sunday start
  return result;
}

function getMonthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function isSameDay(a: Date, b: Date): boolean {
  return formatDate(a) === formatDate(b);
}

function getTimeString(iso: string): string {
  return iso.slice(11, 16);
}

function getStoredView(): CalendarView {
  try {
    const stored = localStorage.getItem("booking-calendar-view");
    if (stored === "day" || stored === "week" || stored === "month") return stored;
  } catch { /* ignore */ }
  return window.innerWidth < 768 ? "day" : "week";
}

// Status config for booking blocks
const BOOKING_STATUS_STYLES: Record<string, { bg: string; dot: string; text: string; border: string }> = {
  confirmed: {
    bg: "rgba(124,58,237,0.10)",
    dot: "var(--brand-primary)",
    text: "var(--brand-primary)",
    border: "rgba(124,58,237,0.25)",
  },
  completed: {
    bg: "rgba(16,185,129,0.10)",
    dot: "#10B981",
    text: "#059669",
    border: "rgba(16,185,129,0.25)",
  },
  no_show: {
    bg: "rgba(239,68,68,0.10)",
    dot: "#EF4444",
    text: "#DC2626",
    border: "rgba(239,68,68,0.25)",
  },
  cancelled: {
    bg: "rgba(113,113,122,0.08)",
    dot: "#71717A",
    text: "#71717A",
    border: "rgba(113,113,122,0.20)",
  },
};

function BookingBlock({ booking, compact = false }: { readonly booking: BookingEntry; readonly compact?: boolean }): ReactNode {
  const [showDetails, setShowDetails] = useState(false);
  const startTime = getTimeString(booking.startTime);
  const endTime = getTimeString(booking.endTime);
  const s = BOOKING_STATUS_STYLES[booking.status] ?? BOOKING_STATUS_STYLES["confirmed"]!;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        style={{
          background: s.bg,
          borderColor: s.border,
          color: s.text,
          transition: "all 0.15s ease",
        }}
        className="w-full text-start rounded-lg border px-2 py-1.5 text-xs cursor-pointer shadow-sm hover:brightness-95 active:scale-[0.98]"
      >
        <div className="flex items-center gap-1 mb-0.5">
          <span
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: s.dot }}
          />
          <span className="font-semibold truncate" dir="ltr">
            {compact ? startTime : `${startTime}–${endTime}`}
          </span>
        </div>
        {!compact && (
          <div className="truncate font-medium opacity-90">{booking.serviceName}</div>
        )}
        <div className="truncate opacity-70">{booking.customerName}</div>
      </button>

      {showDetails && (
        <div
          className="absolute z-20 top-full mt-1.5 end-0 w-72 rounded-xl shadow-2xl p-4 text-sm border"
          style={{
            background: "hsl(var(--card))",
            borderColor: "hsl(var(--border))",
          }}
        >
          {/* Gradient top strip */}
          <div
            className="absolute top-0 inset-x-0 h-0.5 rounded-t-xl"
            style={{ background: "var(--brand-gradient)" }}
          />
          <div className="font-semibold mb-2" style={{ color: "hsl(var(--foreground))" }}>
            {booking.serviceName}
          </div>
          <div className="text-xs mb-1" style={{ color: "hsl(var(--muted-foreground))" }} dir="ltr">
            {startTime} – {endTime}
          </div>
          <div className="mt-2 font-medium" style={{ color: "hsl(var(--foreground))" }}>
            {booking.customerName}
          </div>
          {booking.customerPhone && (
            <div className="text-xs mt-0.5" style={{ color: "hsl(var(--muted-foreground))" }} dir="ltr">
              {booking.customerPhone}
            </div>
          )}
          {booking.notes && (
            <div
              className="mt-2 text-xs rounded-lg p-2"
              style={{
                background: "hsl(var(--muted))",
                color: "hsl(var(--muted-foreground))",
              }}
            >
              {booking.notes}
            </div>
          )}
          <Badge variant="secondary" className="mt-3 text-xs">
            {booking.status}
          </Badge>
        </div>
      )}
    </div>
  );
}

function DayView({ date, bookings }: { readonly date: Date; readonly bookings: readonly BookingEntry[] }): ReactNode {
  const dateStr = formatDate(date);
  const dayBookings = bookings.filter((b) => b.startTime.startsWith(dateStr));

  return (
    <div className="overflow-auto">
      <div
        className="text-center font-semibold py-3 text-sm border-b"
        style={{
          background: "var(--brand-gradient-soft)",
          color: "var(--brand-primary)",
          borderColor: "hsl(var(--border))",
        }}
      >
        {HEBREW_DAYS_FULL[date.getDay()]} {date.getDate()} {HEBREW_MONTHS[date.getMonth()]}
      </div>
      <div className="min-w-0">
        {TIME_SLOTS.map((slot) => {
          const slotBookings = dayBookings.filter((b) => {
            const start = getTimeString(b.startTime);
            const end = getTimeString(b.endTime);
            return start <= slot && end > slot;
          });
          const hasBookings = slotBookings.some((b) => getTimeString(b.startTime) === slot);

          return (
            <div
              key={slot}
              className="flex border-t min-h-[48px] transition-colors"
              style={{
                borderColor: "hsl(var(--border))",
                background: hasBookings ? "rgba(124,58,237,0.03)" : "transparent",
              }}
            >
              <div
                className="w-14 shrink-0 py-2 px-1 text-xs text-center border-e"
                style={{
                  color: "hsl(var(--muted-foreground))",
                  borderColor: "hsl(var(--border))",
                }}
                dir="ltr"
              >
                {slot}
              </div>
              <div className="flex-1 py-1 px-1.5 flex flex-col gap-1">
                {slotBookings
                  .filter((b) => getTimeString(b.startTime) === slot)
                  .map((b) => (
                    <BookingBlock key={b.id} booking={b} />
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ weekStart, bookings }: { readonly weekStart: Date; readonly bookings: readonly BookingEntry[] }): ReactNode {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = new Date();

  return (
    <div className="overflow-x-auto">
      {/* Header */}
      <div
        className="grid grid-cols-[56px_repeat(7,1fr)] border-b sticky top-0 z-10"
        style={{
          background: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
        }}
      >
        <div className="p-2" />
        {days.map((day, i) => {
          const isSaturday = day.getDay() === 6;
          const isToday = isSameDay(day, today);
          return (
            <div
              key={i}
              className="p-2 text-center text-xs font-medium border-e last:border-e-0 transition-colors"
              style={{
                borderColor: "hsl(var(--border))",
                background: isSaturday
                  ? "rgba(113,113,122,0.06)"
                  : isToday
                    ? "rgba(124,58,237,0.06)"
                    : "transparent",
              }}
            >
              <div
                className="uppercase tracking-wide"
                style={{ color: "hsl(var(--muted-foreground))" }}
              >
                {HEBREW_DAYS[day.getDay()]}
              </div>
              <div
                className="text-xl mt-0.5 w-8 h-8 mx-auto flex items-center justify-center rounded-full transition-all"
                style={
                  isToday
                    ? {
                        fontWeight: 700,
                        color: "#fff",
                        background: "var(--brand-primary)",
                        boxShadow: "var(--shadow-brand)",
                      }
                    : { color: "hsl(var(--foreground))" }
                }
              >
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      {TIME_SLOTS.map((slot) => (
        <div
          key={slot}
          className="grid grid-cols-[56px_repeat(7,1fr)] border-b transition-colors hover:bg-[rgba(124,58,237,0.02)]"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <div
            className="py-2 px-1 text-xs text-center border-e"
            style={{
              color: "hsl(var(--muted-foreground))",
              borderColor: "hsl(var(--border))",
            }}
            dir="ltr"
          >
            {slot}
          </div>
          {days.map((day, i) => {
            const dateStr = formatDate(day);
            const isSaturday = day.getDay() === 6;
            const slotBookings = bookings.filter(
              (b) => b.startTime.startsWith(dateStr) && getTimeString(b.startTime) === slot,
            );

            return (
              <div
                key={i}
                className="min-h-[40px] py-0.5 px-0.5 border-e last:border-e-0"
                style={{
                  borderColor: "hsl(var(--border))",
                  background: isSaturday ? "rgba(113,113,122,0.04)" : "transparent",
                }}
              >
                {slotBookings.map((b) => (
                  <BookingBlock key={b.id} booking={b} compact />
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function MonthView({
  monthStart,
  bookings,
  onDayClick,
}: {
  readonly monthStart: Date;
  readonly bookings: readonly BookingEntry[];
  readonly onDayClick: (date: Date) => void;
}): ReactNode {
  const today = new Date();
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0=Sun

  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = Array.from({ length: firstDayOfWeek }, () => null);

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  return (
    <div>
      {/* Day-name header */}
      <div
        className="grid grid-cols-7 border-b"
        style={{
          background: "var(--brand-gradient-soft)",
          borderColor: "hsl(var(--border))",
        }}
      >
        {HEBREW_DAYS.map((name, i) => (
          <div
            key={i}
            className="p-2 text-center text-xs font-semibold uppercase tracking-wide"
            style={{
              color: i === 6 ? "hsl(var(--muted-foreground))" : "var(--brand-primary)",
            }}
          >
            {name}
          </div>
        ))}
      </div>

      {weeks.map((week, wi) => (
        <div
          key={wi}
          className="grid grid-cols-7 border-b"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          {week.map((day, di) => {
            if (day === null) {
              return (
                <div
                  key={di}
                  className="min-h-[80px] border-e last:border-e-0"
                  style={{
                    borderColor: "hsl(var(--border))",
                    background: "rgba(113,113,122,0.03)",
                  }}
                />
              );
            }

            const date = new Date(year, month, day);
            const dateStr = formatDate(date);
            const isSaturday = di === 6;
            const isToday = isSameDay(date, today);
            const dayBookings = bookings.filter((b) => b.startTime.startsWith(dateStr));

            return (
              <button
                key={di}
                type="button"
                onClick={() => onDayClick(date)}
                className="min-h-[80px] p-1.5 border-e last:border-e-0 text-start cursor-pointer group transition-colors hover:bg-[rgba(124,58,237,0.04)]"
                style={{
                  borderColor: "hsl(var(--border))",
                  background: isSaturday ? "rgba(113,113,122,0.04)" : "transparent",
                }}
              >
                <div
                  className="text-sm w-7 h-7 flex items-center justify-center rounded-full transition-all"
                  style={
                    isToday
                      ? {
                          fontWeight: 700,
                          color: "#fff",
                          background: "var(--brand-primary)",
                          boxShadow: "var(--shadow-brand)",
                        }
                      : { color: "hsl(var(--foreground))" }
                  }
                >
                  {day}
                </div>
                {dayBookings.length > 0 && (
                  <div className="mt-1">
                    <span
                      className="inline-flex items-center text-xs rounded-full px-1.5 py-0.5 font-medium"
                      style={{
                        background: "rgba(124,58,237,0.12)",
                        color: "var(--brand-primary)",
                      }}
                    >
                      {dayBookings.length}{" "}
                      {dayBookings.length === 1 ? "תור" : "תורים"}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function BookingCalendarPage(): ReactNode {
  const { accessToken } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>(getStoredView);
  const [bookings, setBookings] = useState<readonly BookingEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const { from, to } = useMemo(() => {
    if (view === "day") {
      return { from: formatDate(currentDate), to: formatDate(currentDate) };
    }
    if (view === "week") {
      const start = getWeekStart(currentDate);
      return { from: formatDate(start), to: formatDate(addDays(start, 6)) };
    }
    // month
    const start = getMonthStart(currentDate);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    return { from: formatDate(start), to: formatDate(end) };
  }, [currentDate, view]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/booking/calendar?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as CalendarResponse;
      if (json.success) {
        const all: BookingEntry[] = [];
        for (const entries of Object.values(json.data.bookings)) {
          all.push(...entries);
        }
        setBookings(all);
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, from, to]);

  useEffect(() => {
    void fetchBookings();
  }, [fetchBookings]);

  const handleViewChange = useCallback((v: string) => {
    const newView = v as CalendarView;
    setView(newView);
    try { localStorage.setItem("booking-calendar-view", newView); } catch { /* ignore */ }
  }, []);

  const navigate = useCallback((dir: number) => {
    setCurrentDate((prev) => {
      if (view === "day") return addDays(prev, dir);
      if (view === "week") return addDays(prev, dir * 7);
      const d = new Date(prev);
      d.setMonth(d.getMonth() + dir);
      return d;
    });
  }, [view]);

  const goToday = useCallback(() => setCurrentDate(new Date()), []);

  const handleMonthDayClick = useCallback((date: Date) => {
    setCurrentDate(date);
    setView("day");
    try { localStorage.setItem("booking-calendar-view", "day"); } catch { /* ignore */ }
  }, []);

  const headerText = useMemo(() => {
    if (view === "day") {
      return `${HEBREW_DAYS_FULL[currentDate.getDay()]} ${currentDate.getDate()} ${HEBREW_MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
    if (view === "week") {
      const start = getWeekStart(currentDate);
      const end = addDays(start, 6);
      return `${start.getDate()}–${end.getDate()} ${HEBREW_MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
    return `${HEBREW_MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  }, [currentDate, view]);

  return (
    <div className="p-4 md:p-6 space-y-4" dir="rtl">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm shrink-0"
            style={{ background: "var(--brand-gradient)" }}
          >
            <CalendarDays className="h-4 w-4 text-white" />
          </div>
          <div>
            <h1
              className="text-lg font-bold leading-tight text-gradient"
              style={{ fontFamily: "var(--font-display)" }}
            >
              לוח שנה
            </h1>
            <p className="text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>
              {bookings.length > 0 ? `${bookings.length} תורים בתצוגה` : "נהל תורים ופגישות"}
            </p>
          </div>
        </div>

        <Tabs value={view} onValueChange={handleViewChange}>
          <TabsList
            className="border"
            style={{
              background: "hsl(var(--muted))",
              borderColor: "hsl(var(--border))",
            }}
          >
            <TabsTrigger
              value="day"
              className="data-[state=active]:shadow-sm"
              style={{ borderRadius: "var(--radius-sm)" }}
            >
              יום
            </TabsTrigger>
            <TabsTrigger
              value="week"
              className="data-[state=active]:shadow-sm"
              style={{ borderRadius: "var(--radius-sm)" }}
            >
              שבוע
            </TabsTrigger>
            <TabsTrigger
              value="month"
              className="data-[state=active]:shadow-sm"
              style={{ borderRadius: "var(--radius-sm)" }}
            >
              חודש
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Date navigation row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(1)}
            className="min-h-[44px] min-w-[44px] transition-colors"
            style={{ borderRadius: "var(--radius-md)" }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            className="min-h-[44px] min-w-[44px] transition-colors"
            style={{ borderRadius: "var(--radius-md)" }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={goToday}
            className="min-h-[44px] font-medium transition-colors"
            style={{
              color: "var(--brand-primary)",
              borderRadius: "var(--radius-md)",
            }}
          >
            היום
          </Button>
        </div>
        <h2
          className="text-sm md:text-base font-semibold"
          style={{ color: "hsl(var(--foreground))" }}
        >
          {headerText}
        </h2>
      </div>

      {/* Calendar card */}
      <Card
        className="overflow-hidden shadow-sm"
        style={{
          borderRadius: "var(--radius-lg)",
          borderColor: "hsl(var(--border))",
          boxShadow: "var(--shadow-card)",
        }}
      >
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="flex flex-col items-center gap-3">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ background: "var(--brand-gradient-soft)" }}
                >
                  <Loader2
                    className="h-6 w-6 animate-spin"
                    style={{ color: "var(--brand-primary)" }}
                  />
                </div>
                <p className="text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>
                  טוען תורים...
                </p>
              </div>
            </div>
          ) : bookings.length === 0 && view !== "month" ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: "var(--brand-gradient-soft)" }}
              >
                <CalendarDays
                  className="h-8 w-8"
                  style={{ color: "var(--brand-primary)", opacity: 0.6 }}
                />
              </div>
              <p
                className="text-base font-semibold"
                style={{ color: "hsl(var(--foreground))" }}
              >
                אין תורים
              </p>
              <p
                className="text-sm mt-1"
                style={{ color: "hsl(var(--muted-foreground))" }}
              >
                היומן הזה פנוי
              </p>
            </div>
          ) : (
            <>
              {view === "day" && <DayView date={currentDate} bookings={bookings} />}
              {view === "week" && (
                <WeekView weekStart={getWeekStart(currentDate)} bookings={bookings} />
              )}
              {view === "month" && (
                <MonthView
                  monthStart={getMonthStart(currentDate)}
                  bookings={bookings}
                  onDayClick={handleMonthDayClick}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
